import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AssetHandoverRecordService } from "services/asset-handover-record";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createAsset = () => ({
  nameSpecification: "",
  assetCode: "",
  countryOfOrigin: "",
  productionYear: "",
  startUseYear: "",
  capacity: "",
  purchasePrice: "",
  transferCost: "",
  trialCost: "",
  otherCost: "",
  originalCost: "",
  technicalDocument: "",
});

const createAccessory = () => ({
  nameSpecification: "",
  unit: "",
  quantity: "",
  value: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round = (value) => Math.round(num(value));
const roundMoney = (value) => Math.round(parseVndIntegerInput(value));
const fmt = (value) => new Intl.NumberFormat("vi-VN").format(roundMoney(value));

export default function AssetHandoverRecordDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({ day: "", month: "", year: "" });
  const [decisionDateParts, setDecisionDateParts] = useState({ day: "", month: "", year: "" });
  const [form, setForm] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    decisionNumber: "",
    decisionBy: "",
    decisionReason: "",
    giverName: "",
    giverTitle: "",
    receiverName: "",
    receiverTitle: "",
    witnessName: "",
    witnessTitle: "",
    witnessDepartment: "",
    handoverLocation: "",
  });
  const [assets, setAssets] = useState([createAsset()]);
  const [accessories, setAccessories] = useState([createAccessory()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await AssetHandoverRecordService.getById(id);
        const data = response.data ? response.data : response;

        setForm({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          decisionNumber: data.decisionNumber || "",
          decisionBy: data.decisionBy || "",
          decisionReason: data.decisionReason || "",
          giverName: data.giverName || "",
          giverTitle: data.giverTitle || "",
          receiverName: data.receiverName || "",
          receiverTitle: data.receiverTitle || "",
          witnessName: data.witnessName || "",
          witnessTitle: data.witnessTitle || "",
          witnessDepartment: data.witnessDepartment || "",
          handoverLocation: data.handoverLocation || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));
        setDecisionDateParts(splitDate(data.decisionDate || ""));
        setAssets(
          (data.assets || []).length
            ? data.assets.map((item) => ({
                ...item,
                purchasePrice: formatVndIntegerInput(item.purchasePrice),
                transferCost: formatVndIntegerInput(item.transferCost),
                trialCost: formatVndIntegerInput(item.trialCost),
                otherCost: formatVndIntegerInput(item.otherCost),
              }))
            : [createAsset()]
        );
        setAccessories(
          (data.accessories || []).length
            ? data.accessories.map((item) => ({
                ...item,
                value: formatVndIntegerInput(item.value),
              }))
            : [createAccessory()]
        );
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản giao nhận TSCĐ");
      }
    })();
  }, [id]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (target, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    const sanitized = field === "year" ? raw.slice(0, max) : raw.slice(-max);
    if (target === "document") {
      setDocumentDateParts((prev) => ({ ...prev, [field]: sanitized }));
      return;
    }
    setDecisionDateParts((prev) => ({ ...prev, [field]: sanitized }));
  };

  const padDatePart = (target, field) => {
    if (field === "year") return;
    const setter = target === "document" ? setDocumentDateParts : setDecisionDateParts;
    setter((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setAssetField = (index, key, value) => {
    setAssets((prev) => {
      const next = [...prev];
      if (["purchasePrice", "transferCost", "trialCost", "otherCost"].includes(key)) {
        next[index] = { ...next[index], [key]: formatVndIntegerInput(value) };
        return next;
      }
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const setAccessoryField = (index, key, value) => {
    setAccessories((prev) => {
      const next = [...prev];
      if (key === "value") {
        next[index] = { ...next[index], [key]: formatVndIntegerInput(value) };
        return next;
      }
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const calculatedAssets = useMemo(
    () =>
      assets.map((item) => {
        const purchasePrice = roundMoney(item.purchasePrice);
        const transferCost = roundMoney(item.transferCost);
        const trialCost = roundMoney(item.trialCost);
        const otherCost = roundMoney(item.otherCost);
        const originalCost = purchasePrice + transferCost + trialCost + otherCost;
        return {
          ...item,
          purchasePrice,
          transferCost,
          trialCost,
          otherCost,
          originalCost,
        };
      }),
    [assets]
  );

  const totalOriginalCost = useMemo(
    () => calculatedAssets.reduce((sum, item) => sum + round(item.originalCost), 0),
    [calculatedAssets]
  );

  const buildPayload = () => {
    const documentDate = composeDate(documentDateParts);
    const decisionDate = composeDate(decisionDateParts);
    if (!documentDate) throw new Error("Ngày biên bản chưa hợp lệ");
    if (!decisionDate) throw new Error("Ngày quyết định chưa hợp lệ");

    const payload = {
      ...form,
      unit: String(form.unit || "").trim(),
      department: String(form.department || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      debitAccount: String(form.debitAccount || "").trim(),
      creditAccount: String(form.creditAccount || "").trim(),
      decisionNumber: String(form.decisionNumber || "").trim(),
      decisionBy: String(form.decisionBy || "").trim(),
      decisionReason: String(form.decisionReason || "").trim(),
      giverName: String(form.giverName || "").trim(),
      giverTitle: String(form.giverTitle || "").trim(),
      receiverName: String(form.receiverName || "").trim(),
      receiverTitle: String(form.receiverTitle || "").trim(),
      witnessName: String(form.witnessName || "").trim(),
      witnessTitle: String(form.witnessTitle || "").trim(),
      witnessDepartment: String(form.witnessDepartment || "").trim(),
      handoverLocation: String(form.handoverLocation || "").trim(),
      documentDate,
      decisionDate,
      assets: calculatedAssets
        .map((item) => ({
          ...item,
          nameSpecification: String(item.nameSpecification || "").trim(),
          assetCode: String(item.assetCode || "").trim(),
          countryOfOrigin: String(item.countryOfOrigin || "").trim(),
          productionYear: String(item.productionYear || "").trim(),
          startUseYear: String(item.startUseYear || "").trim(),
          capacity: String(item.capacity || "").trim(),
          technicalDocument: String(item.technicalDocument || "").trim(),
        }))
        .filter(
          (item) =>
            item.nameSpecification ||
            item.assetCode ||
            item.countryOfOrigin ||
            item.productionYear ||
            item.startUseYear ||
            item.capacity ||
            item.originalCost > 0
        ),
      accessories: accessories
        .map((item) => ({
          nameSpecification: String(item.nameSpecification || "").trim(),
          unit: String(item.unit || "").trim(),
          quantity: round(item.quantity),
          value: roundMoney(item.value),
        }))
        .filter((item) => item.nameSpecification || item.unit || item.quantity > 0 || item.value > 0),
    };

    const required = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["documentNumber", "Số biên bản"],
      ["decisionNumber", "Số quyết định"],
      ["decisionBy", "Đơn vị/cá nhân ra quyết định"],
      ["handoverLocation", "Địa điểm giao nhận TSCĐ"],
    ];
    required.forEach(([key, label]) => {
      if (!payload[key]) throw new Error(`${label} không được để trống`);
    });

    if (!payload.assets.length) {
      throw new Error("Phải có ít nhất 1 dòng tài sản cố định");
    }

    return payload;
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (id) {
        await AssetHandoverRecordService.update(id, payload);
        toast.success("Cập nhật biên bản giao nhận TSCĐ thành công");
      } else {
        await AssetHandoverRecordService.create(payload);
        toast.success("Tạo biên bản giao nhận TSCĐ thành công");
      }
      navigation("/templates/asset-handover-record");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Không thể lưu biên bản giao nhận TSCĐ");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const html = getPrintTemplate({
      data: {
        ...form,
        documentDate: composeDate(documentDateParts),
        decisionDate: composeDate(decisionDateParts),
        assets: calculatedAssets,
        accessories,
        totalOriginalCost,
      },
    });
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const s = {
    page: {
      fontFamily: '"Times New Roman", Times, serif',
      fontSize: 15,
      color: "#000",
      background: "#fff",
      padding: 16,
    },
    input: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: 15, fontFamily: "inherit", background: "transparent" },
    th: { border: "1px solid #000", padding: "4px", textAlign: "center", fontWeight: 700, verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: "4px", verticalAlign: "middle" },
    tdInput: { width: "100%", border: "none", background: "transparent", outline: "none", fontFamily: "inherit", fontSize: 14 },
    btn: { marginRight: 8, marginTop: 6 },
    tdCenter: { textAlign: "center" },
    tdRight: { textAlign: "right" },
    tdLeft: { textAlign: "left" },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.page}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div><b>Đơn vị:</b> <input disabled={containView} style={{ ...s.input, width: 200 }} value={form.unit} onChange={(e) => setField("unit", e.target.value)} /></div>
                    <div><b>Bộ phận:</b> <input disabled={containView} style={{ ...s.input, width: 200 }} value={form.department} onChange={(e) => setField("department", e.target.value)} /></div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div><b>Mẫu số 01 - TSCĐ</b></div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>(Ban hành theo Thông tư số 200/2014/TT-BTC</div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>Ngày 22/12/2014 của Bộ Tài chính)</div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "8px 0 4px" }}>BIÊN BẢN GIAO NHẬN TSCĐ</h2>
                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input disabled={containView} style={{ ...s.input, width: 32, textAlign: "center", margin: "0 4px" }} value={documentDateParts.day} onChange={(e) => setDateField("document", "day", e.target.value)} onBlur={() => padDatePart("document", "day")} />
                  tháng
                  <input disabled={containView} style={{ ...s.input, width: 32, textAlign: "center", margin: "0 4px" }} value={documentDateParts.month} onChange={(e) => setDateField("document", "month", e.target.value)} onBlur={() => padDatePart("document", "month")} />
                  năm
                  <input disabled={containView} style={{ ...s.input, width: 50, textAlign: "center", margin: "0 4px" }} value={documentDateParts.year} onChange={(e) => setDateField("document", "year", e.target.value)} />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 18, marginBottom: 8 }}>
                  <div>Số: <input disabled={containView} style={{ ...s.input, width: 90 }} value={form.documentNumber} onChange={(e) => setField("documentNumber", e.target.value)} /></div>
                  <div>Nợ: <input disabled={containView} style={{ ...s.input, width: 90 }} value={form.debitAccount} onChange={(e) => setField("debitAccount", e.target.value)} /></div>
                  <div>Có: <input disabled={containView} style={{ ...s.input, width: 90 }} value={form.creditAccount} onChange={(e) => setField("creditAccount", e.target.value)} /></div>
                </div>

                <div style={{ marginBottom: 8 }}>
                  Căn cứ Quyết định số: <input disabled={containView} style={{ ...s.input, width: 120 }} value={form.decisionNumber} onChange={(e) => setField("decisionNumber", e.target.value)} />
                  &nbsp; ngày
                  <input disabled={containView} style={{ ...s.input, width: 32, textAlign: "center", margin: "0 4px" }} value={decisionDateParts.day} onChange={(e) => setDateField("decision", "day", e.target.value)} onBlur={() => padDatePart("decision", "day")} />
                  tháng
                  <input disabled={containView} style={{ ...s.input, width: 32, textAlign: "center", margin: "0 4px" }} value={decisionDateParts.month} onChange={(e) => setDateField("decision", "month", e.target.value)} onBlur={() => padDatePart("decision", "month")} />
                  năm
                  <input disabled={containView} style={{ ...s.input, width: 50, textAlign: "center", margin: "0 4px" }} value={decisionDateParts.year} onChange={(e) => setDateField("decision", "year", e.target.value)} />
                  của <input disabled={containView} style={{ ...s.input, width: 220 }} value={form.decisionBy} onChange={(e) => setField("decisionBy", e.target.value)} />
                </div>

                <div style={{ marginBottom: 8 }}>
                  Về việc bàn giao TSCĐ{" "}
                  <input disabled={containView} style={{ ...s.input, width: "62%" }} value={form.decisionReason} onChange={(e) => setField("decisionReason", e.target.value)} />
                </div>
                <div style={{ marginBottom: 8 }}>Ban giao nhận TSCĐ gồm:</div>
                <div style={{ marginBottom: 8 }}>
                  - Ông/Bà <input disabled={containView} style={{ ...s.input, width: 160 }} value={form.giverName} onChange={(e) => setField("giverName", e.target.value)} />
                  &nbsp; chức vụ <input disabled={containView} style={{ ...s.input, width: 120 }} value={form.giverTitle} onChange={(e) => setField("giverTitle", e.target.value)} />
                  &nbsp; Đại diện bên giao
                </div>
                <div style={{ marginBottom: 8 }}>
                  - Ông/Bà <input disabled={containView} style={{ ...s.input, width: 160 }} value={form.receiverName} onChange={(e) => setField("receiverName", e.target.value)} />
                  &nbsp; chức vụ <input disabled={containView} style={{ ...s.input, width: 120 }} value={form.receiverTitle} onChange={(e) => setField("receiverTitle", e.target.value)} />
                  &nbsp; Đại diện bên nhận
                </div>
                <div style={{ marginBottom: 8 }}>
                  - Ông/Bà <input disabled={containView} style={{ ...s.input, width: 160 }} value={form.witnessName} onChange={(e) => setField("witnessName", e.target.value)} />
                  &nbsp; chức vụ <input disabled={containView} style={{ ...s.input, width: 120 }} value={form.witnessTitle} onChange={(e) => setField("witnessTitle", e.target.value)} />
                  &nbsp; Đại diện <input disabled={containView} style={{ ...s.input, width: 140 }} value={form.witnessDepartment} onChange={(e) => setField("witnessDepartment", e.target.value)} />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Địa điểm giao nhận TSCĐ: <input disabled={containView} style={{ ...s.input, width: "65%" }} value={form.handoverLocation} onChange={(e) => setField("handoverLocation", e.target.value)} />
                </div>
                <div style={{ marginBottom: 6 }}>Xác nhận việc giao nhận TSCĐ như sau:</div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={s.th} rowSpan={2}>STT</th>
                        <th style={s.th} rowSpan={2}>Tên, ký hiệu quy cách (cấp hạng TSCĐ)</th>
                        <th style={s.th} rowSpan={2}>Số hiệu TSCĐ</th>
                        <th style={s.th} rowSpan={2}>Nước sản xuất (XD)</th>
                        <th style={s.th} rowSpan={2}>Năm sản xuất</th>
                        <th style={s.th} rowSpan={2}>Năm đưa vào sử dụng</th>
                        <th style={s.th} rowSpan={2}>Công suất (diện tích thiết kế)</th>
                        <th style={s.th} colSpan={5}>Tính nguyên giá tài sản cố định</th>
                        <th style={s.th} rowSpan={2}>Tài liệu kỹ thuật kèm theo</th>
                      </tr>
                      <tr>
                        <th style={s.th}>Giá mua (ZSX)</th>
                        <th style={s.th}>Chi phí vận chuyển</th>
                        <th style={s.th}>Chi phí chạy thử</th>
                        <th style={s.th}>...</th>
                        <th style={s.th}>Nguyên giá TSCĐ</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        <th style={s.th}>4</th>
                        <th style={s.th}>5</th>
                        <th style={s.th}>6</th>
                        <th style={s.th}>7</th>
                        <th style={s.th}>8</th>
                        <th style={s.th}>E</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((item, index) => (
                        <tr key={index}>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 42 }}>{index + 1}</td>
                          <td style={{ ...s.td, ...s.tdLeft, minWidth: 180 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "left" }} value={item.nameSpecification} onChange={(e) => setAssetField(index, "nameSpecification", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 90 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.assetCode} onChange={(e) => setAssetField(index, "assetCode", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 90 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.countryOfOrigin} onChange={(e) => setAssetField(index, "countryOfOrigin", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 80 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.productionYear} onChange={(e) => setAssetField(index, "productionYear", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 100 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.startUseYear} onChange={(e) => setAssetField(index, "startUseYear", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 90 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.capacity} onChange={(e) => setAssetField(index, "capacity", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight, minWidth: 100 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.purchasePrice} onChange={(e) => setAssetField(index, "purchasePrice", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight, minWidth: 110 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.transferCost} onChange={(e) => setAssetField(index, "transferCost", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight, minWidth: 105 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.trialCost} onChange={(e) => setAssetField(index, "trialCost", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight, minWidth: 90 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.otherCost} onChange={(e) => setAssetField(index, "otherCost", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight, minWidth: 110 }}>{fmt(calculatedAssets[index]?.originalCost || 0)}</td>
                          <td style={{ ...s.td, ...s.tdCenter, minWidth: 120 }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.technicalDocument} onChange={(e) => setAssetField(index, "technicalDocument", e.target.value)} /></td>
                        </tr>
                      ))}
                      <tr>
                        <td style={s.td}></td>
                        <td style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                        <td style={s.td}></td>
                        <td style={s.td}></td>
                        <td style={s.td}></td>
                        <td style={s.td}></td>
                        <td style={{ ...s.td, textAlign: "right", fontWeight: 700 }}>{fmt(totalOriginalCost)}</td>
                        <td style={{ ...s.td, textAlign: "center" }}>x</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {!containView && (
                  <div style={{ marginTop: 8, marginBottom: 6 }}>
                    <SoftButton size="small" color="dark" variant="outlined" sx={s.btn} onClick={() => setAssets((prev) => [...prev, createAsset()])}>THÊM DÒNG</SoftButton>
                    <SoftButton size="small" color="error" variant="outlined" sx={s.btn} onClick={() => setAssets((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))}>Xoá dòng TSCĐ</SoftButton>
                  </div>
                )}

                <div style={{ marginTop: 10, marginBottom: 6, fontWeight: 700, textAlign: "center" }}>DỤNG CỤ, PHỤ TÙNG KÈM THEO</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={s.th}>Số thứ tự</th>
                        <th style={s.th}>Tên, quy cách dụng cụ, phụ tùng</th>
                        <th style={s.th}>Đơn vị tính</th>
                        <th style={s.th}>Số lượng</th>
                        <th style={s.th}>Giá trị</th>
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessories.map((item, index) => (
                        <tr key={index}>
                          <td style={{ ...s.td, ...s.tdCenter }}>{index + 1}</td>
                          <td style={{ ...s.td, ...s.tdLeft }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "left" }} value={item.nameSpecification} onChange={(e) => setAccessoryField(index, "nameSpecification", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdCenter }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "center" }} value={item.unit} onChange={(e) => setAccessoryField(index, "unit", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.quantity} onChange={(e) => setAccessoryField(index, "quantity", e.target.value)} /></td>
                          <td style={{ ...s.td, ...s.tdRight }}><input disabled={containView} style={{ ...s.tdInput, textAlign: "right" }} value={item.value} onChange={(e) => setAccessoryField(index, "value", e.target.value)} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!containView && (
                  <div>
                    <SoftButton size="small" color="dark" variant="outlined" sx={s.btn} onClick={() => setAccessories((prev) => [...prev, createAccessory()])}>THÊM DÒNG</SoftButton>
                    <SoftButton size="small" color="error" variant="outlined" sx={s.btn} onClick={() => setAccessories((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))}>Xoá dòng phụ tùng</SoftButton>
                  </div>
                )}

              </SoftBox>

              <SoftBox p={2} display="flex" justifyContent="space-between">
                <SoftButton color="secondary" onClick={() => navigation("/templates/asset-handover-record")}>
                  Trở về
                </SoftButton>
                <div>
                  {containView && (
                    <SoftButton variant="gradient" color="success" onClick={handlePrint} sx={{ mr: 1 }}>
                      In Bảng
                    </SoftButton>
                  )}
                  {!containView && (
                    <SoftButton color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                      {id ? "Lưu" : "Tạo"}
                    </SoftButton>
                  )}
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
